import { format } from "date-fns";
import React, { useEffect } from "react";

import { useToastStore } from "../components/modules/Common/ToastProvider/store/useToastStore";
import HNSColors from "../components/ui library/HNSColors";
import { emitSessionWarning } from "../services/session-warning-channel";
import { SSE_EVENT_TYPES } from "../services/sse-service";

import useLiveEventTimestamp from "./useLiveEventTimestamp";
import useSseConnection from "./useSseConnection";

const buildAiSummaryWindow = (callSummary) => {
  const start = callSummary?.call_log?.start_time;
  const end = callSummary?.call_log?.end_time;

  if (!start || !end) {
    return null;
  }

  try {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const dateLabel = format(startDate, "P");
    const startLabel = format(startDate, "p").toLowerCase();
    const endLabel = format(endDate, "p").toLowerCase();
    return (
      <>
        <strong>{dateLabel}</strong> at <strong>{startLabel}</strong> - <strong>{endLabel}</strong>
      </>
    );
  } catch (error) {
    return null;
  }
};

const useLiveServerEvents = ({
  accessToken,
  manageConnection = true,
  permissions = [],
  onConnectionError,
  onCallSummaryNavigate,
  onAnyEventReceived,
  onRegisterStopper,
  onNotificationCount,
}) => {
  const requestToast = useToastStore((state) => state.requestToast);
  const { addEventListener, service } = useSseConnection({
    accessToken,
    manageConnection,
    onConnectionError,
    onRegisterStopper,
  });
  const { shouldHandleEvent, updateLastEventTimestamp } = useLiveEventTimestamp();

  const canViewCallSummary = permissions?.includes("call_log.view_callsummary");

  useEffect(() => {
    if (typeof onAnyEventReceived !== "function") {
      return undefined;
    }

    if (typeof service?.subscribeAll === "function") {
      return service.subscribeAll(({ eventType, payload, rawEvent }) => {
        onAnyEventReceived(payload, eventType, rawEvent);
      });
    }

    if (typeof addEventListener !== "function") {
      return undefined;
    }

    return addEventListener("message", (payload, rawEvent) => {
      const eventType = rawEvent?.type || payload?.type || "message";
      onAnyEventReceived(payload, eventType, rawEvent);
    });
  }, [addEventListener, onAnyEventReceived, service]);

  useEffect(() => {
    if (typeof addEventListener !== "function") {
      return undefined;
    }

    const unsubscribe = addEventListener("message", (payload) => {
      updateLastEventTimestamp(payload?.timestamp);
    });

    return unsubscribe;
  }, [addEventListener, updateLastEventTimestamp]);

  useEffect(() => {
    if (typeof addEventListener !== "function") {
      return undefined;
    }

    const unsubscribe = addEventListener(SSE_EVENT_TYPES.NOTIFICATION_SUMMARY, (payload) => {
      if (!shouldHandleEvent(payload?.timestamp)) {
        return;
      }
      updateLastEventTimestamp(payload?.timestamp);
      const unseenCount = Number(payload?.notificationData?.unseen_count);
      if (!Number.isFinite(unseenCount) || unseenCount <= 0) {
        return;
      }
      if (typeof onNotificationCount === "function") {
        onNotificationCount(unseenCount);
      }
    });

    return unsubscribe;
  }, [addEventListener, requestToast, shouldHandleEvent, updateLastEventTimestamp]);

  useEffect(() => {
    if (!canViewCallSummary || typeof onCallSummaryNavigate !== "function") {
      return undefined;
    }

    if (typeof addEventListener !== "function") {
      return undefined;
    }

    const unsubscribe = addEventListener(SSE_EVENT_TYPES.CALL_SUMMARY, (payload) => {
      if (!shouldHandleEvent(payload?.timestamp)) {
        return;
      }
      updateLastEventTimestamp(payload?.timestamp);
      const callSummary = payload?.callSummary;
      if (!callSummary) {
        return;
      }

      const summaryWindow = buildAiSummaryWindow(callSummary);
      requestToast({
        message:
          summaryWindow !== null ? (
            <div>Your AI Call Summary from {summaryWindow}.</div>
          ) : (
            "Your AI Call Summary is ready."
          ),
        navigation: {
          label: "View AI summary",
          onClick: onCallSummaryNavigate,
          txtColor: HNSColors.secondary[500],
        },
      });
    });

    return unsubscribe;
  }, [
    addEventListener,
    canViewCallSummary,
    onCallSummaryNavigate,
    requestToast,
    shouldHandleEvent,
    updateLastEventTimestamp,
  ]);

  useEffect(() => {
    if (typeof addEventListener !== "function") {
      return undefined;
    }

    const unsubscribe = addEventListener(SSE_EVENT_TYPES.SESSION_EXPIRY, (payload = {}) => {
      emitSessionWarning(payload);
    });

    return unsubscribe;
  }, [addEventListener]);
};

export default useLiveServerEvents;
